/**
 * Detecção de dispositivo (computador × mobile) e preferências de controle na tela.
 *
 * Permite identificar se o jogador está em um celular/tablet para exibir os
 * controles virtuais transparentes na tela, ao mesmo tempo em que permite
 * ocultá-los automaticamente quando um joystick físico (gamepad) for ativado.
 */

import { useCallback, useEffect, useState } from 'react';

export type TouchControlsPref = 'auto' | 'sempre' | 'nunca';

const TOUCH_PREF_KEY = 'oz_touch_controls_pref';

/**
 * Detecta se o dispositivo atual é móvel (smartphone ou tablet).
 */
export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent || (navigator as any).vendor || (typeof window !== 'undefined' && (window as any).opera) || '';
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i;
  const isMobileUA = mobileRegex.test(ua);

  const hasTouch = (typeof window !== 'undefined' && 'ontouchstart' in window) ||
    (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0);
  const isCoarse = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  const isSmallScreen = typeof window !== 'undefined' && window.innerWidth <= 900;

  return isMobileUA || (hasTouch && (isCoarse || isSmallScreen));
}

/**
 * Lê a preferência guardada de controles na tela ('auto' | 'sempre' | 'nunca').
 */
export function loadTouchControlsPref(): TouchControlsPref {
  const storage = typeof localStorage !== 'undefined' ? localStorage : (typeof globalThis.localStorage !== 'undefined' ? globalThis.localStorage : null);
  if (!storage) return 'auto';
  try {
    const raw = storage.getItem(TOUCH_PREF_KEY);
    if (raw === 'sempre' || raw === 'nunca' || raw === 'auto') return raw;
  } catch {
    // Ignora erro de storage restrito
  }
  return 'auto';
}

/**
 * Salva a preferência de controles na tela.
 */
export function saveTouchControlsPref(pref: TouchControlsPref): void {
  const storage = typeof localStorage !== 'undefined' ? localStorage : (typeof globalThis.localStorage !== 'undefined' ? globalThis.localStorage : null);
  if (!storage) return;
  try {
    storage.setItem(TOUCH_PREF_KEY, pref);
  } catch {
    // Ignora erro
  }
}

/**
 * Hook para reagir à mudança de viewport e ao tipo de dispositivo.
 */
export function useDeviceType() {
  const [isMobile, setIsMobile] = useState<boolean>(() => isMobileDevice());
  const [touchPref, setTouchPrefState] = useState<TouchControlsPref>(() => loadTouchControlsPref());

  useEffect(() => {
    const check = () => {
      setIsMobile(isMobileDevice());
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', check);
      window.addEventListener('orientationchange', check);
      return () => {
        window.removeEventListener('resize', check);
        window.removeEventListener('orientationchange', check);
      };
    }
  }, []);

  const setTouchPref = useCallback((pref: TouchControlsPref) => {
    setTouchPrefState(pref);
    saveTouchControlsPref(pref);
  }, []);

  /**
   * Decide se os botões na tela devem ser exibidos:
   * - 'sempre': exibe independente de joystick ou aparelho.
   * - 'nunca': não exibe.
   * - 'auto': exibe se estiver no mobile E se nenhum joystick físico estiver conectado.
   */
  const shouldShowTouchControls = useCallback(
    (gamepadConnected: boolean): boolean => {
      if (touchPref === 'nunca') return false;
      if (touchPref === 'sempre') return true;
      // No modo auto: apenas em mobile e some quando joystick estiver ativado
      return isMobile && !gamepadConnected;
    },
    [touchPref, isMobile],
  );

  return {
    isMobile,
    touchPref,
    setTouchPref,
    shouldShowTouchControls,
  };
}

/**
 * Tenta travar a orientação da tela em modo paisagem (horizontal) no Fullscreen.
 * Suportado nativamente em navegadores mobile modernos (Android Chrome, etc.).
 */
export async function lockLandscapeOrientation(): Promise<void> {
  if (typeof screen === 'undefined') return;

  try {
    const orientation = screen.orientation as any;
    if (orientation && typeof orientation.lock === 'function') {
      await orientation.lock('landscape').catch(async () => {
        await orientation.lock('landscape-primary').catch(() => {});
      });
      return;
    }
    const scr = screen as any;
    if (typeof scr.lockOrientation === 'function') {
      scr.lockOrientation('landscape');
    } else if (typeof scr.mozLockOrientation === 'function') {
      scr.mozLockOrientation('landscape');
    } else if (typeof scr.msLockOrientation === 'function') {
      scr.msLockOrientation('landscape');
    }
  } catch {
    // Falha silenciosa em navegadores de desktop ou sem permissão
  }
}

/**
 * Libera a trava de orientação ao sair da tela cheia.
 */
export function unlockOrientation(): void {
  if (typeof screen === 'undefined') return;

  try {
    if (screen.orientation && typeof screen.orientation.unlock === 'function') {
      screen.orientation.unlock();
      return;
    }
    const scr = screen as any;
    if (typeof scr.unlockOrientation === 'function') {
      scr.unlockOrientation();
    } else if (typeof scr.mozUnlockOrientation === 'function') {
      scr.mozUnlockOrientation();
    } else if (typeof scr.msUnlockOrientation === 'function') {
      scr.msUnlockOrientation();
    }
  } catch {
    // Falha silenciosa
  }
}
